import { favouriteGame } from '@/apis/casino/games';
import { TGameListItem } from '@/apis/hub88';
import useFindMatchingGame from '@/hooks/biz/casino/useFindMatchingGame';
import useLoginModal from '@/hooks/biz/useLoginModal';
import {
  default as keysFactory,
  default as queryKeysFactory,
} from '@/reactQueryKeys/keysFactory';
import queryClient from '@/reactQueryKeys/queryClient';
import { gameListSelector } from '@/state/globalSettings';
import { isLoggedIn } from '@/state/userInfo';
import useTheme from '@/theme/useTheme';
import { Star, StarBorder } from '@mui/icons-material';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai/index';
import { useTranslation } from 'next-i18next';

const FavoriteStar: React.FC<{
  gameId: string;
  source?: TGameListItem['source'];
}> = ({ gameId }) => {
  const [userLogin, showModal] = useLoginModal();

  const { theme } = useTheme();
  const [t] = useTranslation();

  const matchingGame = useFindMatchingGame(gameId);

  const favouriteGameParams = {
    gameId,
    provider: matchingGame?.provider,
    /**
     * 2 === Hub88
     */
  };

  const {
    data: favoriteData,
    refetch,
    isLoading: isLoadingFavourited,
  } = useQuery({
    queryKey: keysFactory.casino.isGameFavorited(gameId),
    queryFn: () => {
      return favouriteGame({
        ...favouriteGameParams,
        action: 'view',
      });
    },
    enabled: !!userLogin,
  });

  const isFavorite = favoriteData?.data?.favorite?.follow || false;
  const { mutate, isLoading: isLoadingFavourite } = useMutation(
    () => {
      return favouriteGame({
        ...favouriteGameParams,
        action: isFavorite ? 'unfollow' : 'follow',
      });
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(
          keysFactory.casino.isGameFavorited(gameId),
          data,
        );
        queryClient.invalidateQueries(
          queryKeysFactory.casino.queryFavoriteGames(),
        );
      },
    },
  );

  const StarComponent =
    isLoadingFavourite || !matchingGame
      ? (props) => <CircularProgress {...props} size={'small'} />
      : isFavorite
        ? Star
        : StarBorder;

  if (!matchingGame) return null;

  return (
    <Tooltip title={t('Favorite')}>
      <Box
        sx={{
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '24px',
          width: '24px',
        }}
      >
        <StarComponent
          sx={{
            display: 'inline-block',
            cursor: 'pointer',
            color: isFavorite ? theme.palette.primary.main : undefined,
            '&:hover': {
              color: theme.palette.primary.main,
            },
            height: '24px',
            width: '24px',
          }}
          onClick={() => {
            if (!userLogin) return showModal();
            mutate();
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default FavoriteStar;
